/**
 * FDCart — centralized cart state, deduped fetches, event bus.
 * Vanilla JS; no dependencies.
 */
(function (global) {
  'use strict';

  var cart = null;
  var fetchPromise = null;
  var fetchId = 0;
  var fetchJoinable = false;
  var listeners = new Set();
  var lastCompositionFingerprint = '';
  // Cart writes seen by the add handler's fetch/XHR wrapper (add, change, update, clear).
  var writesStarted = 0;
  var writesDone = 0;
  var lastWriteStartedAt = 0;
  // The newest write started while another ran: the server can apply them in either order.
  var newestOverlapped = false;
  var newestStartOutside = 0;
  // Writes that have not reported their end yet, by sequence number.
  var openWrites = {};
  var WRITE_MAX_AGE = 15000;
  var knownAttributes = null;
  var fetchStartedAt = 0;
  // Fingerprint of the newest cart known to match the server, and the write count then.
  var currentFingerprint = '';
  var currentAtWrite = -1;
  var currentSince = 0;
  var lastWrittenCart = null;
  // Bumped when the cart may have changed outside this page's view (another tab,
  // a page restored from the back-forward cache, a page left hidden).
  var outsideChanges = 0;
  var idleWaiters = [];
  var CURRENT_MAX_AGE = 600000;
  var tabChannel = null;
  var otherTabWrites = 0;
  // A cart's version: the newest write it surely includes (its own, or the newest ended before its read).
  var newestEnded = 0;
  var cartVersions = typeof WeakMap === 'function' ? new WeakMap() : null;

  function fingerprintComposition(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return c.items
      .map(function (item) {
        return [item.key, item.quantity, item.variant_id, item.selling_plan || ''].join(':');
      })
      .sort()
      .join('|');
  }

  function fingerprintProductSet(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return c.items
      .map(function (item) {
        return String(item.product_id);
      })
      .sort()
      .join('|');
  }

  function notify() {
    listeners.forEach(function (fn) {
      try {
        fn(cart);
      } catch (e) {
        console.error('[FDCart] listener error', e);
      }
    });

    if (typeof document !== 'undefined') {
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: cart }, bubbles: true })
      );
    }
  }

  function setCart(next) {
    cart = next || null;
    if (cart) {
      lastCompositionFingerprint = fingerprintComposition(cart);
    }
    notify();
    return cart;
  }

  function getCart() {
    return cart;
  }

  function subscribe(fn) {
    listeners.add(fn);
    return function unsubscribe() {
      listeners.delete(fn);
    };
  }

  function compositionChanged(prev, next) {
    return fingerprintComposition(prev) !== fingerprintComposition(next);
  }

  function productSetChanged(prev, next) {
    return fingerprintProductSet(prev) !== fingerprintProductSet(next);
  }

  // Returns the write's sequence number, for noteWriteEnd.
  function noteWriteStart() {
    newestOverlapped = writesStarted > writesDone;
    writesStarted += 1;
    lastWriteStartedAt = Date.now();
    newestStartOutside = outsideChanges;
    var seq = writesStarted;
    openWrites[seq] = true;
    // A write that never reports its end stops counting as running after 15s.
    setTimeout(function () {
      if (openWrites[seq]) noteWriteEnd(null, seq);
    }, WRITE_MAX_AGE);
    tellOtherTabs();
    return seq;
  }

  // A write's cart is current only when it is the newest write and none is still
  // running: an older or overlapping write's cart can miss a line.
  function noteWriteEnd(written, seq) {
    // A write that started before this store loaded was never counted.
    if (seq == null) {
      forgetCurrent();
      return;
    }
    // A write that ends after its max age changed the cart unseen.
    if (!openWrites[seq]) {
      forgetCurrent();
      return;
    }
    delete openWrites[seq];
    writesDone = Math.min(writesDone + 1, writesStarted);
    if (seq > newestEnded) newestEnded = seq;
    stampCart(written, seq);
    var newest = seq === writesStarted;
    if (newest && writesDone === writesStarted &&
        written && written.attributes && typeof written.attributes === 'object' && Array.isArray(written.items)) {
      knownAttributes = written.attributes;
      markCurrent(written);
      lastWrittenCart = written;
    }
    if (writesDone === writesStarted) {
      tellOtherTabs();
      var waiters = idleWaiters;
      idleWaiters = [];
      waiters.forEach(function (resolve) { resolve(); });
    }
  }

  function stampCart(c, version) {
    if (!cartVersions || !c || typeof c !== 'object' || typeof version !== 'number') return c;
    cartVersions.set(c, version);
    return c;
  }

  function cartVersion(c) {
    if (!cartVersions || !c || typeof c !== 'object' || !cartVersions.has(c)) return null;
    return cartVersions.get(c);
  }

  function readVersion() {
    return newestEnded;
  }

  function markCurrent(c) {
    currentFingerprint = fingerprintState(c);
    currentAtWrite = writesStarted;
    currentSince = Date.now();
  }

  function forgetCurrent() {
    outsideChanges += 1;
    currentAtWrite = -1;
    knownAttributes = null;
    lastWrittenCart = null;
  }

  // Resolves once no cart write is running, or after maxWait ms.
  function whenWritesIdle(maxWait) {
    if (writesStarted === writesDone) return Promise.resolve();
    return new Promise(function (resolve) {
      idleWaiters.push(resolve);
      if (maxWait > 0) setTimeout(resolve, maxWait);
    });
  }

  function tellOtherTabs() {
    if (!tabChannel) return;
    try {
      tabChannel.postMessage('write');
    } catch (e) {}
  }

  function fingerprintCodes(c) {
    if (!Array.isArray(c.discount_codes)) return '';
    return c.discount_codes
      .map(function (entry) {
        return entry && entry.code ? String(entry.code) : '';
      })
      .sort()
      .join(',');
  }

  function fingerprintAttributes(c) {
    var attributes = c.attributes && typeof c.attributes === 'object' ? c.attributes : {};
    return Object.keys(attributes)
      .sort()
      .map(function (key) {
        return key + '=' + String(attributes[key]);
      })
      .join('&');
  }

  function fingerprintState(c) {
    if (!c || !Array.isArray(c.items)) return '';
    return [fingerprintComposition(c), c.item_count, c.total_price, c.token || '', fingerprintCodes(c), fingerprintAttributes(c)].join('#');
  }

  // True when c matches the newest server cart this page saw and no write has
  // started since, so it can stand in for a fresh /cart.js read.
  function isCartCurrent(c) {
    if (!c || writesStarted !== writesDone || currentAtWrite !== writesStarted) return false;
    if (Date.now() - currentSince > CURRENT_MAX_AGE) return false;
    return fingerprintState(c) === currentFingerprint;
  }

  // The cached cart, or the cart the last write returned (an app's write included),
  // when it matches the server. Null means read the cart.
  function getCurrentCart() {
    if (cart && isCartCurrent(cart)) return cart;
    if (lastWrittenCart && isCartCurrent(lastWrittenCart)) return lastWrittenCart;
    return null;
  }

  function getWriteState() {
    return {
      started: writesStarted,
      pending: writesStarted - writesDone,
      startedAt: lastWriteStartedAt,
      overlapped: newestOverlapped,
      // The cart may have changed outside this page's writes (another tab, page hidden) since the newest write started.
      outside: outsideChanges !== newestStartOutside,
    };
  }

  // Attributes from the latest write response or clean read, else from the cached cart.
  function getKnownAttributes() {
    if (currentAtWrite < 0 || Date.now() - currentSince > CURRENT_MAX_AGE) return null;
    if (knownAttributes) return knownAttributes;
    return cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : null;
  }

  // A forced read joins the read in flight only when no cart write started since
  // that read began, so it can never hand back a cart from before a write.
  function fetchCart(force) {
    if (!force && cart && !fetchPromise) {
      return Promise.resolve(cart);
    }
    if (fetchPromise && (!force || (fetchJoinable && fetchStartedAt === writesStarted))) {
      return fetchPromise;
    }

    var id = ++fetchId;
    var startedAt = writesStarted;
    var outsideAt = outsideChanges;
    var clean = writesStarted === writesDone;
    var version = newestEnded;
    fetchStartedAt = startedAt;
    fetchJoinable = clean;
    var pending = fetch('/cart.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        stampCart(data, version);
        if (clean && startedAt === writesStarted && writesStarted === writesDone && outsideAt === outsideChanges) {
          markCurrent(data);
          if (data && data.attributes && typeof data.attributes === 'object') knownAttributes = data.attributes;
        }
        var held = cartVersion(cart);
        if (id === fetchId && (held === null || held <= version)) setCart(data);
        return data;
      })
      .finally(function () {
        if (id === fetchId) fetchPromise = null;
      });
    fetchPromise = pending;
    return pending;
  }

  function invalidate() {
    cart = null;
  }

  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') forgetCurrent();
    });
  }
  if (typeof global.addEventListener === 'function') {
    global.addEventListener('pageshow', function (event) {
      if (event && event.persisted) forgetCurrent();
    });
  }
  try {
    if (typeof BroadcastChannel === 'function') {
      tabChannel = new BroadcastChannel('fd-cart');
      tabChannel.onmessage = function () {
        otherTabWrites += 1;
        forgetCurrent();
      };
    }
  } catch (e) {
    tabChannel = null;
  }

  global.FDCart = {
    getCart: getCart,
    setCart: setCart,
    noteWriteStart: noteWriteStart,
    noteWriteEnd: noteWriteEnd,
    getWriteState: getWriteState,
    getKnownAttributes: getKnownAttributes,
    isCartCurrent: isCartCurrent,
    getCurrentCart: getCurrentCart,
    whenWritesIdle: whenWritesIdle,
    stampCart: stampCart,
    cartVersion: cartVersion,
    readVersion: readVersion,
    getOtherTabWrites: function () {
      return otherTabWrites;
    },
    subscribe: subscribe,
    fetchCart: fetchCart,
    invalidate: invalidate,
    fingerprintComposition: fingerprintComposition,
    fingerprintProductSet: fingerprintProductSet,
    compositionChanged: compositionChanged,
    productSetChanged: productSetChanged,
    getLastCompositionFingerprint: function () {
      return lastCompositionFingerprint;
    },
  };
})(typeof window !== 'undefined' ? window : this);
